import { useEffect, useRef } from "react";
import { Animated, View } from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";
import { MarkdownRenderer } from "@memorang/ui";
import { Text } from "react-native-paper";

import { useActiveScenario } from "../hooks/useActiveScenario";
import type { TranscriptMessage } from "../types";
import { MessageActionBar } from "./MessageActionBar";
import { PersonaAvatar } from "./PersonaAvatar";

const TypingDots = () => {
	const theme = useAppTheme();
	const { scenario } = useActiveScenario();
	const dotsRef = useRef([
		new Animated.Value(0.3),
		new Animated.Value(0.3),
		new Animated.Value(0.3),
	]);

	useEffect(() => {
		const animations = dotsRef.current.map((dot, i) =>
			Animated.loop(
				Animated.sequence([
					Animated.delay(i * 160),
					Animated.timing(dot, {
						toValue: 1,
						duration: 300,
						useNativeDriver: true,
					}),
					Animated.timing(dot, {
						toValue: 0.3,
						duration: 300,
						useNativeDriver: true,
					}),
					Animated.delay((2 - i) * 160),
				]),
			),
		);
		for (const animation of animations) {
			animation.start();
		}
		return () => {
			for (const animation of animations) {
				animation.stop();
			}
		};
	}, []);

	return (
		<View
			accessibilityLabel={`${scenario.persona.name} is typing`}
			style={{
				flexDirection: "row",
				alignItems: "center",
				gap: 4,
				minHeight: 22,
			}}
		>
			{dotsRef.current.map((dot, i) => (
				<Animated.View
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static list
					key={i}
					style={{
						width: 7,
						height: 7,
						borderRadius: 4,
						backgroundColor: theme.colors.onSurfaceVariant,
						opacity: dot,
					}}
				/>
			))}
		</View>
	);
};

export const MessageBubble = ({
	message,
	isStreaming,
}: {
	message: TranscriptMessage;
	isStreaming?: boolean;
}) => {
	const theme = useAppTheme();
	if (message.role === "mentor") {
		return (
			<View
				style={{
					flexDirection: "row",
					justifyContent: "flex-end",
					alignItems: "flex-start",
					marginVertical: 6,
				}}
			>
				<View
					style={{
						flexShrink: 1,
						maxWidth: 450,
						backgroundColor: theme.colors.primary,
						paddingVertical: 12,
						paddingHorizontal: 16,
						borderRadius: theme.roundness * 4,
						borderBottomRightRadius: theme.roundness,
					}}
				>
					<MarkdownRenderer
						text={message.text}
						fontSize={16}
						paragraphMarginVertical={0}
						customStyles={{ text: { color: theme.colors.onPrimary } }}
					/>
				</View>
			</View>
		);
	}

	return (
		<View
			style={{
				flexDirection: "row",
				gap: 10,
				alignItems: "flex-start",
				marginVertical: 6,
			}}
		>
			<PersonaAvatar size={32} />
			<View style={{ flexShrink: 1, maxWidth: "100%" }}>
				<View
					style={{
						backgroundColor: theme.colors.surfaceVariant,
						borderWidth: 1,
						borderColor: theme.colors.outlineVariant,
						padding: 14,
						borderRadius: theme.roundness * 4,
						borderBottomLeftRadius: theme.roundness,
					}}
				>
					{message.text ? (
						<MarkdownRenderer
							text={message.text}
							fontSize={16}
							paragraphMarginVertical={0}
							customStyles={{ text: { color: theme.colors.onSurface } }}
						/>
					) : isStreaming ? (
						<TypingDots />
					) : (
						<Text
							variant="bodyLarge"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							…
						</Text>
					)}
				</View>
				{!isStreaming && message.text ? (
					<MessageActionBar message={message} />
				) : null}
			</View>
		</View>
	);
};
